
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src994476363/src/vl-app/pages/patients/timeline.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { Row, Col, Button } from 'antd';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import format from 'vl-common/src/lib/formatDate';
import SelfDischarge from '@components/Patients/SelfDischarge/SelfDischarge';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import UploadDocumentModal from 'vl-common/src/components/UploadDocument/UploadDocumentModal';
import { CaseTimelineItem } from 'vl-common/src/schemas/getUsersTimeline';
import useUser from '@vl-core/hooks/useUser';
import Header from '@components/Layouts/Header/Header';

const PageHead = dynamic(() => import('@components/Layouts/PageHead/PageHead'));
const Tab = dynamic(() => import('@components/UI/Tabs/Tab'));
const HistoryTimeline = dynamic(() => import('@components/History/Timeline'));
const LetterDocuments = dynamic(() => import('@components/Documents/LetterDocuments'));
const AssessmentTable = dynamic(() => import('@components/Assessments/AssessmentTable'));

interface PatientsTimelineProps {
  caseId: string;
  appId: string;
  statusId: string;
}

const PatientsTimeline = ({ caseId, appId, statusId }: PatientsTimelineProps) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const [title, setTitle] = useState<{ item_condition: string; case_created_date: Date }>();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [documentsRefresh, setDocumentsRefresh] = useState(0);
  const user = useUser();

  const { data: timelineItems = [] } = useQuery({
    queryKey: ['userTimelineHistory', caseId],
    queryFn: async () => {
      const result = await dispatch(actions.userTimelineHistory('USER_TIMELINEAPPOINTMENTHISTORY', caseId));
      if (result.type === actionTypes.USER_TIMELINEAPPOINTMENTHISTORY_SUCCESS) {
        return result.payload;
      }
      return [];
    }
  });

  // if at least one of the case timeline items has already been discharged
  // then the patient cannot self discharge from that case
  const isCaseDischarged = useMemo(
    () =>
      !!timelineItems?.find((t: CaseTimelineItem) => t.item_type_code === 'DISCHARGE' && t.item_level === 'case') ||
      false,
    [timelineItems]
  );

  return (
    <div className="page-container">
      <Header pageType="patients" pageTitle="Timeline" />
      <section className="section-layout patient" id="page-content">
        <main className="container">
          <Row gutter={[8, 8]} align="top">
            <Col flex="auto">
              <PageHead
                backUrl="/patients"
                backArrow
                backText={`Condition: ${title?.item_condition}`}
                backDesc={`Case Created: ${
                  title
                    ? format(title.case_created_date, 'Long Date with day-of-week - Thursday, March 3rd, 2022')
                    : 'unknown'
                }`}
              />
            </Col>
            <Col flex="none">
              <Row gutter={[0, 8]}>
                {user?.physitrack_link && (
                  <Button
                    style={{ marginRight: '20px' }}
                    type="primary"
                    onClick={() => user.physitrack_link && window.open(user.physitrack_link)}
                    data-testid="physitrack-link"
                  >
                    {t('Exercise program')}
                  </Button>
                )}
                <SelfDischarge canDischarge={!isCaseDischarged} caseId={Number(caseId)} />
                <Button
                  disabled={statusId !== 'null'}
                  type="primary"
                  onClick={() => {
                    setUploadModalVisible(true);
                  }}
                  data-testid="upload-button"
                >
                  {t('Upload')}
                </Button>
              </Row>
            </Col>
          </Row>
          <Tab
            tabNames={['Appointment History', 'Documents', 'Assessments']}
            tabContent={[
              <HistoryTimeline pageTitle={setTitle} key="1" caseId={caseId} />,
              <LetterDocuments key="2" caseId={Number(caseId)} appId={Number(appId)} refresh={documentsRefresh} />,
              <AssessmentTable key="3" caseId={Number(caseId)} />
            ]}
          />
        </main>
        <UploadDocumentModal
          visible={uploadModalVisible}
          setVisible={setUploadModalVisible}
          appId={appId}
          patientId={user.user_guid}
          afterDocumentUpload={() => setDocumentsRefresh((refresh) => refresh + 1)}
        />
      </section>
    </div>
  );
};

const PatientsTimelineWrapper = () => {
  const { t } = useTranslation('common');
  const initialised = useInitialisedStore();
  const { query } = useRouter();
  const appId = Buffer.from(`${query.app}`, 'base64').toString();
  const caseId = Buffer.from(`${query.req}`, 'base64').toString();
  const statusId = Buffer.from(`${query.status}`, 'base64').toString();

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  if ((appId && !Number(appId)) || (caseId && !Number(caseId))) return null;

  return <PatientsTimeline appId={appId} caseId={caseId} statusId={statusId} />;
};

export default PatientsTimelineWrapper;
export const Route = createFileRoute('/patients/timeline')({ component: PatientsTimelineWrapper });
